import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _sort from 'lodash/sortBy';
import _some from 'lodash/some';
import isNumber from 'is-number';
import log from 'loglevel';

import { getRequiredExams, getBanner } from 'Domain';
import { MAJOR } from 'Util/Constants';

import RecommendedCoursesView from './RecommendedCoursesView';
import { adsConditions } from './data';

const filterNoPercentile = ({ percentile }) => percentile !== null || percentile !== undefined;

const filterNoMapAds = ({ subjectCode }) => subjectCode && adsConditions[subjectCode];

const getImageAndUrl = examResult => {
  const { id, subjectCode, percentile } = examResult;
  const subjectAds = adsConditions[subjectCode];
  const ads = subjectAds.find(
    ads =>
      ads.maxPercentile >= percentile ||
      ads.maxPercentile === null ||
      ads.maxPercentile === undefined,
  );
  if (!ads) return { noAds: true };
  const { bannerUrl, linkUrl: href } = ads;

  return { id, img: 'https://tcas-assets.skooldio.com/mockexam/courses/' + bannerUrl, href };
};

const getDisplayAds = (examResults = [], maxAds) => {
  // filter out no percentile, no ads
  const filteredExamResults = examResults.filter(filterNoPercentile).filter(filterNoMapAds);
  // Sort by percentile low -> high
  const sortedExamResults = _sort(filteredExamResults, examResult => examResult.percentile);
  // mapping image and url by percentile condition
  const displayData = sortedExamResults.map(getImageAndUrl);
  const displayDataFiltered = displayData.filter(ads => !ads.noAds);
  // Slice to only 6 cards
  const displayAds = maxAds ? displayDataFiltered.slice(0, maxAds) : displayDataFiltered;
  return displayAds;
};

/**
 * OD requires following ads:
 * คอร์สแนะนำ O-Net
 * คอร์สแนะนำ วิชาสามัญ
 * คอร์สแนะนำ PAT
 * คอร์สแนะนำ GAT
 *
 * We borrow these subject codes: ONETM6MATH, SAMANMATH, PAT1, GAT
 *
 **/

const ADS_OD_2020_JAN_M6 = [
  { subjectCode: '04' },
  { subjectCode: '39' },
  { subjectCode: '71' },
  { subjectCode: '85' },
];

const ADS_OD_2020_JAN_M3 = [
  { subjectCode: '94' },
  { subjectCode: '95' },
  { subjectCode: '91' },
  { subjectCode: '93' },
];

const DEFAULT_ADS = {
  M3: ADS_OD_2020_JAN_M3,
  ART_MATH: ADS_OD_2020_JAN_M6,
  // [
  //   { subjectCode: '85' },
  //   { subjectCode: '71' },
  //   { subjectCode: '72' },
  //   { subjectCode: '04' },
  // ],
  ART_LANG: ADS_OD_2020_JAN_M6,
  // [
  //   { subjectCode: '85' },
  //   { subjectCode: '71' },
  //   { subjectCode: '04' },
  //   { subjectCode: '05' },
  // ],
  DEFAULT: ADS_OD_2020_JAN_M6,
  // [
  //   { subjectCode: '85' },
  //   { subjectCode: '71' },
  //   { subjectCode: '72' },
  //   { subjectCode: '73' },
  // ],
  AVAILABLE: [
    '94',
    '95',
    '91',
    '93',
    '85',
    '71',
    '72',
    '73',
    '00',
    '49',
    '39',
    '89',
    '59',
    '69',
    '99',
    '04',
    '05',
  ],
};

const getDefaultAds = studentMajor => {
  const { SCIENCE, ART_LANG, ART_MATH, OTHER } = MAJOR;
  switch (studentMajor) {
    case ART_MATH:
      return DEFAULT_ADS.ART_MATH;
    case ART_LANG:
      return DEFAULT_ADS.ART_LANG;
    case SCIENCE:
    case OTHER:
    default:
      return DEFAULT_ADS.DEFAULT;
  }
};

const calculateExamScoreForShowAds = examResult => {
  const { score, averageScore } = examResult;
  if (isNumber(score) && isNumber(averageScore)) {
    return {
      ...examResult,
      calculatedExamResultsScore: ((score - averageScore) / averageScore) * 100,
    };
  } else {
    log.debug('NaN was found in examScore or avgScore');
  }
};

const sortExamResultByScore = (examResult, nextExamResult, keyForSort) =>
  //sort by score asc
  examResult[keyForSort] - nextExamResult[keyForSort];

const getTop4DisplayAds = (examResults = [], studentMajor, deptCriteria = [], maxAds = 4) => {
  log.debug('getTop4DisplayAds examResults:', examResults);
  // calculatedExamResults by formula
  const calculatedExamResults = examResults
    .map(calculateExamScoreForShowAds)
    .filter(examResult => examResult && DEFAULT_ADS.AVAILABLE.includes(examResult.subjectCode))
    .sort((examResult, nextExamResult) =>
      sortExamResultByScore(examResult, nextExamResult, 'calculatedExamResultsScore'),
    );

  // prepared for weight of departmentCriteria
  // const entranceScoreMatch = deptCriteria
  //   .map(subject => {
  //     const subjectMatch = calculatedExamResults.find(
  //       examResult => examResult.subjectCode === subject.subjectCode,
  //     );
  //     const scoreWithWeight = subjectMatch.calculatedExamResultsScore * subject.weight;
  //     return { ...subjectMatch, scoreWithWeight };
  //   })
  //   .sort((examResult, nextExamResult) =>
  //     sortExamResultByScore(examResult, nextExamResult, 'scoreWithWeight'),
  //   );

  // slice display for show only 4
  const top4ExamResult = calculatedExamResults.slice(0, maxAds);

  const examAds =
    top4ExamResult.length < maxAds
      ? [...top4ExamResult, ...getDefaultAds(studentMajor).slice(0, maxAds - top4ExamResult.length)]
      : top4ExamResult;

  // getBanner from config
  const displayAds = examAds.map(examResult => {
    const { image, link } = getBanner(examResult.subjectCode, 'overall');
    return {
      id: examResult.id,
      img: image,
      href: link,
      noAds: false,
      subjectCode: examResult.subjectCode,
    };
  });

  return displayAds;
};

const getFixedAds = (examResults = []) => {
  const isM3 = _some(examResults, examResult =>
    ADS_OD_2020_JAN_M3.map(ads => ads.subjectCode).includes(examResult.subjectCode),
  );

  log.debug('getFixedAds', examResults, isM3);

  const adBannerList = isM3 ? ADS_OD_2020_JAN_M3 : ADS_OD_2020_JAN_M6;

  return adBannerList.map(({ subjectCode }) => {
    const { image, link } = getBanner(subjectCode, 'overall');
    return {
      img: image,
      href: link,
      noAds: false,
      subjectCode: subjectCode,
    };
  });
};

const preAds = [
  // {
  //   id: 'tcastermock',
  //   img: 'https://tcas-assets.skooldio.com/mockexam/courses/tcastermock.png',
  //   href: 'https://store.tcaster.net/?coupon-code=tcaster15',
  // },
  {
    id: 'tcasterapp',
    img: 'https://tcas-assets.skooldio.com/mockexam/courses/tcasterapp.png',
    href: 'http://bit.ly/APP4MOCK',
  },
];

const generalAds = [];

export default class RecommendedCourses extends Component {
  static propTypes = {
    data: PropTypes.arrayOf(
      PropTypes.shape({
        subjectCode: PropTypes.string.isRequired,
        averageScore: PropTypes.number,
        examCardId: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        percentile: PropTypes.number, // We use only this field
        ranking: PropTypes.number,
      }),
    ),
    maxAds: PropTypes.number,
  };

  static defaultProps = {
    data: [],
  };

  render() {
    const { data, maxAds, myMockWishDepartmentData, studentMajor } = this.props;
    // const adsList = getDisplayAds(data, maxAds);
    // const adsWithGeneralAds = [...preAds, ...adsList, ...generalAds];
    const top4AdsList = getFixedAds(data);
    return <RecommendedCoursesView courses={top4AdsList} />;
  }
}
